import { FincaClimaDataDiarioResponse } from "../modulos/clima/response/FincaClimaDataDiarioResponse";
import { FincaClimaDataHorarioResponse } from "../modulos/clima/response/FincaClimaDataHorarioResponse";
import { formatDiaSolo, toISODate } from "./fechaUtils";

/** Categoría simplificada de la condición climática, derivada de
 *  precipitación/probabilidad/nubosidad (réplica de la lógica original del
 *  servidor). Fuente única de la condición para ambos modos (horario y
 *  diario no traen un campo de condición equivalente entre sí); también se
 *  usa para elegir la ilustración de fondo. */
type ClimaCategoria = "despejado" | "nublado" | "lluvia";

function climaCategoria(
  precipitation: number,
  precipitationProbability: number,
  totalcloudcover: number,
): ClimaCategoria {
  if (precipitation > 0.2 && precipitationProbability > 60) return "lluvia";
  if (totalcloudcover > 40) return "nublado";
  return "despejado";
}

const CATEGORIA_LABELS: Record<ClimaCategoria, string> = {
  despejado: "Despejado",
  nublado: "Nublado",
  lluvia: "Lluvia",
};

/** Etiqueta legible de la condición climática por categoría (horario y
 *  diario por igual). */
export function climaCategoriaLabel(
  precipitation: number,
  precipitationProbability: number,
  totalcloudcover: number,
): string {
  return CATEGORIA_LABELS[
    climaCategoria(precipitation, precipitationProbability, totalcloudcover)
  ];
}

/** Ícono Phosphor por categoría (lista de pronóstico, un ícono chico por día
 *  en vez de la ilustración de fondo completa). */
const CATEGORIA_ICONS: Record<ClimaCategoria, string> = {
  despejado: "ph-sun",
  nublado: "ph-cloud",
  lluvia: "ph-cloud-rain",
};

/** Clase del ícono Phosphor de la condición climática por categoría. */
export function climaCategoriaIcon(
  precipitation: number,
  precipitationProbability: number,
  totalcloudcover: number,
): string {
  return CATEGORIA_ICONS[
    climaCategoria(precipitation, precipitationProbability, totalcloudcover)
  ];
}

/** Rutas de las ilustraciones de fondo (`assets/clima/`) por categoría y
 *  franja (día/noche). No hay "noche despejado" con sol, se usa `noche.png`. */
const BACKGROUND_ROUTES: Record<
  ClimaCategoria,
  { dia: string; noche: string }
> = {
  despejado: {
    dia: "assets/clima/dia_soleado.png",
    noche: "assets/clima/noche.png",
  },
  nublado: {
    dia: "assets/clima/dia_nublado.png",
    noche: "assets/clima/noche_nublado.png",
  },
  lluvia: {
    dia: "assets/clima/dia_lluvia.png",
    noche: "assets/clima/noche_lluvia.png",
  },
};

/** Datos mínimos para elegir la ilustración de fondo (comunes a horario y
 *  diario; el diario no trae `isdaylight`, se calcula con `isDaylightNow`). */
export type ClimaBackgroundInput = {
  precipitation: number;
  precipitationProbability: number;
  totalcloudcover: number;
  /** 1 = día, 0 = noche. */
  isdaylight: number;
};

/** Ruta de la ilustración de fondo (`assets/clima/`) para los datos dados. */
export function climaBackgroundRoute(input: ClimaBackgroundInput): string {
  const categoria = climaCategoria(
    input.precipitation,
    input.precipitationProbability,
    input.totalcloudcover,
  );
  return input.isdaylight
    ? BACKGROUND_ROUTES[categoria].dia
    : BACKGROUND_ROUTES[categoria].noche;
}

/** Heurística de día/noche por la hora local del reloj (6–18 = día), para el
 *  diario, que no trae `isdaylight` (a diferencia del horario, que sí lo
 *  trae calculado por meteoblue con el amanecer/atardecer real de la finca). */
export function isDaylightNow(): boolean {
  const hour = new Date().getHours();
  return hour >= 6 && hour < 18;
}

/** Registros (horarios o diarios) de hoy (fecha local), pasados y
 *  pronosticados — usado por los gráficos de tendencia del día. */
export function itemsDeHoy<T extends { fecha: string }>(items: T[]): T[] {
  const hoy = toISODate(new Date());
  return items.filter((item) => toISODate(new Date(item.fecha)) === hoy);
}

/** Registros horarios desde hoy en adelante (excluye días pasados) — usado
 *  por los gráficos de tendencia multi-día del Forecast. */
export function itemsDesdeHoy<T extends { fecha: string }>(items: T[]): T[] {
  const hoy = toISODate(new Date());
  return items.filter((item) => toISODate(new Date(item.fecha)) >= hoy);
}

/** Agregado diario calculado en el cliente a partir del horario — reemplaza
 *  al endpoint `/diario` (se está retirando del servidor). Además de los
 *  campos que ya traía ese endpoint, suma `evapotranspiracionSum` (el diario
 *  original nunca tuvo evapotranspiración, solo el horario). */
export type FincaClimaDataDiarioCalculado = FincaClimaDataDiarioResponse & {
  /** Suma de evapotranspiración de las horas del día (mm). */
  evapotranspiracionSum: number;
};

/** Valor más frecuente (moda) de un arreglo; empate → el primero encontrado
 *  con esa frecuencia máxima. */
function moda(valores: number[]): number {
  const conteos = new Map<number, number>();
  let masFrecuente = valores[0];
  let maxConteo = 0;
  for (const valor of valores) {
    const conteo = (conteos.get(valor) ?? 0) + 1;
    conteos.set(valor, conteo);
    if (conteo > maxConteo) {
      maxConteo = conteo;
      masFrecuente = valor;
    }
  }
  return masFrecuente;
}

/** Agrupa registros horarios por día (fecha local) y calcula los agregados
 *  diarios — reemplazo del endpoint `/diario`, retirado del servidor. Los
 *  horarios ya vienen ordenados ascendente por fecha, así que el orden por
 *  día se preserva. Réplica exacta de la semántica del diario original:
 *  `windspeed`/`totalcloudcover` promedio, `precipitationSum` suma,
 *  `precipitationProbability` máximo, `sunshinetime` suma de minutos ÷ 60,
 *  `winddirection` moda (valor más frecuente del día). */
export function agruparHorarioPorDia(
  items: FincaClimaDataHorarioResponse[],
): FincaClimaDataDiarioCalculado[] {
  const porDia = new Map<string, FincaClimaDataHorarioResponse[]>();
  for (const item of items) {
    const fecha = toISODate(new Date(item.fecha));
    const grupo = porDia.get(fecha);
    if (grupo) grupo.push(item);
    else porDia.set(fecha, [item]);
  }

  const promedio = (valores: number[]): number =>
    valores.reduce((suma, v) => suma + v, 0) / valores.length;
  const suma = (valores: number[]): number =>
    valores.reduce((s, v) => s + v, 0);

  return Array.from(porDia.entries()).map(([fecha, horas]) => ({
    fecha,
    temperatureMin: Math.min(...horas.map((h) => h.temperature)),
    temperatureMax: Math.max(...horas.map((h) => h.temperature)),
    precipitationSum: suma(horas.map((h) => h.precipitation)),
    precipitationProbability: Math.max(
      ...horas.map((h) => h.precipitationProbability),
    ),
    relativehumidityMin: Math.min(...horas.map((h) => h.relativehumidity)),
    relativehumidityMax: Math.max(...horas.map((h) => h.relativehumidity)),
    windspeed: promedio(horas.map((h) => h.windspeed)),
    winddirection: moda(horas.map((h) => h.winddirection)),
    totalcloudcover: promedio(horas.map((h) => h.totalcloudcover)),
    sunshinetime: suma(horas.map((h) => h.sunshinetime)) / 60,
    evapotranspiracionSum: suma(horas.map((h) => h.evapotranspiration)),
  }));
}

/** true si el item en `index` empieza un día distinto al de la hora anterior
 *  (el índice 0 siempre cuenta como inicio de día). */
function esInicioDeDia(items: { fecha: string }[], index: number): boolean {
  if (index === 0) return true;
  const actual = toISODate(new Date(items[index].fecha));
  const anterior = toISODate(new Date(items[index - 1].fecha));
  return actual !== anterior;
}

/** Índices donde empieza un nuevo día (fecha distinta a la hora anterior),
 *  para pintar líneas verticales de separación por día en gráficos
 *  multi-día por hora (Forecast "rango"). No incluye el índice 0 (el borde
 *  del gráfico, no un límite de día real que valga la pena marcar). */
export function indicesInicioDia(items: { fecha: string }[]): number[] {
  const indices: number[] = [];
  for (let i = 1; i < items.length; i++) {
    if (esInicioDeDia(items, i)) indices.push(i);
  }
  return indices;
}

/** Etiqueta de eje para gráficos multi-día por hora (Forecast "rango"):
 *  imprime el día ("07 Jul") centrado bajo su bloque de horas (no en la
 *  primera hora, que es donde va la línea de separación de
 *  `indicesInicioDia`), vacío en el resto. */
export function etiquetaDiaCentrada(
  items: { fecha: string }[],
  index: number,
): string {
  const limites = [0, ...indicesInicioDia(items), items.length];
  for (let i = 0; i < limites.length - 1; i++) {
    const desde = limites[i];
    const hasta = limites[i + 1];
    const centro = desde + Math.floor((hasta - desde) / 2);
    if (index === centro) return formatDiaSolo(items[desde].fecha);
  }
  return "";
}

/** Índices cuya hora local coincide con alguna de `horas` (ej. [6, 12, 18]) —
 *  para marcar divisores/etiquetas fijas en el gráfico de un solo día
 *  (Current, modo "dia"). */
export function indicesPorHora(
  items: { fecha: string }[],
  horas: number[],
): number[] {
  const indices: number[] = [];
  items.forEach((item, index) => {
    if (horas.includes(new Date(item.fecha).getHours())) indices.push(index);
  });
  return indices;
}

/** Parsea el campo `rainspot` (string separado por comas con la
 *  precipitación de cada celda de la grilla 7x7 alrededor de la finca, dato
 *  de meteoblue) a un arreglo de números. */
export function parseRainspot(rainspot: string): number[] {
  return rainspot
    .split(",")
    .map((valor) => Number(valor.trim()))
    .filter((valor) => !Number.isNaN(valor));
}

/** Color de celda del mapa de "rainspot" según su valor. Réplica de la
 *  lógica original del servidor (`GetRainspotDivCssClass`). */
export function rainspotValueToColor(value: number): string {
  if (value <= 0.02) return "#d3d3d3";
  if (value <= 0.2) return "#a7fefea9";
  if (value <= 1.5) return "#28f9f2";
  if (value <= 5) return "#34a1f5";
  return "#0077ff";
}
